import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import QuickEntryFormModal from '@/components/feature/QuickEntryFormModal';
import { useAuth } from '@/components/feature/AuthContext';
import { useSectionNav } from '@/hooks/useSectionNav';
import { useHeroTone } from '@/components/feature/HeroToneContext';

const LOGO_URL =
  'https://static.readdy.ai/image/40df70cb7fb470e01d31fe19c944f5a5/54dbeaa09eb58c85d24f735fbf1d9a65.png';

interface NavLink {
  label: string;
  href?: string;
  children?: { label: string; to: string }[];
}

const navLinks: NavLink[] = [
  { label: '首页', href: '#home' },
  { label: '平台介绍', href: '#intro' },
  {
    label: '课程中心',
    children: [
      { label: '名师课程', to: '/courses/master' },
      { label: '名院课程', to: '/courses/hospital' },
    ],
  },
  { label: '会议培训', href: '#activities' },
  { label: '医院专科', href: '#hospitals' },
  { label: '优势病种', href: '#diseases' },
  { label: '成果转化', href: '#achievements' },
  { label: '合作伙伴', href: '#partners' },
];

function maskPhone(phone: string) {
  if (phone.length !== 11) return phone;
  return `${phone.slice(0, 3)} **** ${phone.slice(7)}`;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cooperationOpen, setCooperationOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const { goToSection } = useSectionNav();
  const { user, openLogin, logout } = useAuth();
  const location = useLocation();
  const { pathname: heroTonePath, tone: heroToneValue } = useHeroTone();

  // Only trust the reported tone when it belongs to the current route;
  // otherwise fall back to the dark hero default (e.g. the home page).
  const heroTone = heroTonePath === location.pathname ? heroToneValue : 'dark';
  const lightTop = heroTone === 'light';

  const memberPhone = user?.mobile || user?.userName || '';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [userMenuOpen]);

  const textColor = scrolled || lightTop ? 'text-foreground-800' : 'text-background-50';

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setMenuOpen(false);
  };

  const memberBtnClass = scrolled || lightTop
    ? 'border-background-300 text-foreground-800 hover:bg-background-100'
    : 'border-background-50/40 text-background-50 hover:bg-background-50/10';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200 py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="w-full px-4 md:px-8 flex items-center justify-between">
        <a href="#home" onClick={(e) => goToSection(e, '#home')} className="flex items-center gap-3 cursor-pointer">
          <span className="flex items-center justify-center bg-background-50 rounded-lg h-12 px-2 py-1">
            <img src={LOGO_URL} alt="元合 VITA" title="元合VITA 中西医结合智慧生态平台" className="h-10 w-auto" />
          </span>
          <span className="hidden lg:flex flex-col leading-tight">
            <span className={`font-heading font-bold text-base ${scrolled || lightTop ? 'text-foreground-900' : 'text-background-50'}`}>
              中西医结合智慧生态平台
            </span>
            <span className={`text-xs ${scrolled || lightTop ? 'text-foreground-500' : 'text-background-100/80'}`}>
              元合 VITA · 整合型健康服务平台
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-0.5">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.label} className="relative group">
                <button
                  type="button"
                  className={`inline-flex items-center gap-0.5 px-2.5 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap hover:bg-primary-500/10 hover:text-primary-500 ${textColor}`}
                >
                  {link.label}
                  <i className="ri-arrow-down-s-line text-base"></i>
                </button>
                <div className="absolute left-0 top-full pt-2 hidden group-hover:block">
                  <div className="w-40 rounded-lg border border-background-200 bg-background-50 py-1.5">
                    {link.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => goToSection(e, link.href ?? '#')}
                className={`px-2.5 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap hover:bg-primary-500/10 hover:text-primary-500 ${textColor}`}
              >
                {link.label}
              </a>
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          {memberPhone ? (
            <div className="relative hidden lg:block" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${memberBtnClass}`}
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-user-3-line text-sm"></i>
                </span>
                {maskPhone(memberPhone)}
                <i className={`ri-arrow-down-s-line transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 py-2 rounded-lg border bg-background-50 border-background-200">
                  <div className="px-4 py-2 border-b border-background-200">
                    <p className="text-xs text-foreground-500">当前账号</p>
                    <p className="text-sm font-medium text-foreground-900">{maskPhone(memberPhone)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex items-center w-full gap-2 px-4 py-2.5 text-sm text-left transition-colors cursor-pointer text-foreground-700 hover:bg-background-100"
                  >
                    <i className="ri-logout-box-r-line"></i>
                    退出登录
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => openLogin()}
              className={`hidden lg:inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${memberBtnClass}`}
            >
              <i className="ri-user-line"></i>
              会员登录
            </button>
          )}

          <button
            type="button"
            onClick={() => setCooperationOpen(true)}
            className="hidden lg:inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-customer-service-2-line"></i>
            生态合作
          </button>
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
              scrolled || lightTop ? 'text-foreground-900' : 'text-background-50'
            }`}
          >
            <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden mt-2 mx-4 rounded-lg bg-background-50 border border-background-200 py-2">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.label}>
                <div className="px-4 pt-3 pb-1 text-xs font-semibold text-foreground-500">{link.label}</div>
                {link.children.map((child) => (
                  <Link
                    key={child.to}
                    to={child.to}
                    onClick={() => setMenuOpen(false)}
                    className="block px-6 py-3 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  setMenuOpen(false);
                  goToSection(e, link.href ?? '#');
                }}
                className="block px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                {link.label}
              </a>
            ),
          )}
          {memberPhone ? (
            <div className="px-4 py-3 mt-1 border-t border-background-200">
              <p className="text-xs text-foreground-500">当前账号</p>
              <p className="text-sm font-medium text-foreground-900">{maskPhone(memberPhone)}</p>
              <button
                type="button"
                onClick={handleLogout}
                className="mt-2 text-sm text-primary-600 cursor-pointer"
              >
                退出登录
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                openLogin();
              }}
              className="block mx-2 my-2 px-4 py-3 text-sm font-medium text-center border rounded-md cursor-pointer border-background-300 text-foreground-800 w-[calc(100%-1rem)]"
            >
              会员登录
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setCooperationOpen(true);
            }}
            className="block m-2 mx-auto text-center bg-primary-500 text-background-50 text-sm font-medium px-4 py-3 rounded-md cursor-pointer w-[calc(100%-1rem)]"
          >
            生态合作
          </button>
        </div>
      )}

      <QuickEntryFormModal type={cooperationOpen ? 'cooperation' : null} onClose={() => setCooperationOpen(false)} />
    </header>
  );
}